import { Box, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { PageScaffold, type PageScaffoldProps } from './PageScaffold';
import { designTokens } from '../tokens';

type StandardPageProps = Omit<PageScaffoldProps, 'template' | 'prominent'>;
export type ResourcePageProps = Omit<StandardPageProps, 'presentation' | 'bodyMode' | 'footer' | 'editActions'>;

/** 목록은 추가 surface 없이 본문에 배치하고 남은 작업 공간을 채웁니다. */
export function ResourcePage({ children, ...props }: ResourcePageProps) {
  return <PageScaffold {...props} template="resource" presentation="plain" bodyMode="fill">
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
  </PageScaffold>;
}

export type InformationPageProps = Omit<StandardPageProps, 'presentation' | 'bodyMode' | 'footer' | 'editActions'>;

/** 독립 정보 카드를 같은 중립 canvas에 배치합니다. */
export function InformationPage(props: InformationPageProps) {
  return <PageScaffold {...props} template="information" presentation="sectioned" bodyMode="flow" />;
}

export type GuidePageProps = Omit<StandardPageProps, 'presentation' | 'bodyMode' | 'footer' | 'editActions'> & {
  /** 기존 페이지의 scroll·presentation 계약입니다. 모든 페이지는 공통 중립 canvas를 사용합니다. */
  presentation?: 'plain' | 'sectioned';
  /** 빈 작업 공간처럼 본문이 화면의 남은 높이를 채워야 할 때 사용합니다. */
  fill?: boolean;
};

/** 선택·단계·작업 진입을 자연스러운 문서 흐름으로 표시합니다. */
export function GuidePage({ fill = false, presentation = 'plain', ...props }: GuidePageProps) {
  return <PageScaffold {...props} template="guide" presentation={presentation} bodyMode={fill ? 'fill' : 'flow'} />;
}

export type PageSectionsProps = {
  children: ReactNode;
  spacing?: 'standard' | 'grouped';
  /** 페이지 전체의 조회 영역에 사용하며, 카드 내부에서는 생략합니다. */
  fill?: boolean;
};

/** 같은 층위의 카드와 큰 업무 영역의 간격을 구분합니다. */
export function PageSections({ children, spacing = 'standard', fill = false }: PageSectionsProps) {
  return <Stack data-testid="page-sections" sx={{
    minWidth: 0,
    ...(fill ? { height: '100%', minHeight: 0 } : {}),
    gap: `${spacing === 'grouped' ? designTokens.layout.pageSectionGroupGap : designTokens.layout.pageSectionGap}px`,
  }}>{children}</Stack>;
}

export type EditPageProps = Omit<StandardPageProps, 'bodyMode' | 'footer'> & {
  /** 도면처럼 내부 영역이 각각 스크롤하는 편집기는 남은 작업 공간을 채웁니다. */
  fill?: boolean;
  /** 편집 중 저장·취소를 함께 전달합니다. 보기 모드에서는 생략합니다. */
  editActions?: ReactNode;
};

/** 긴 편집 본문만 스크롤하고 저장·취소는 상단 제목 영역에 유지합니다. */
export function EditPage({ editActions, fill = false, width = 'standard', ...props }: EditPageProps) {
  return <PageScaffold
    {...props}
    template="edit"
    width={width}
    bodyMode={fill ? 'fill' : editActions ? 'scroll' : 'flow'}
    editActions={editActions}
  />;
}
